import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { AcceptJobRequest, AddJobRequest, CompleteJobRequest, IJobByStatus, IMarketplaceJob, Job, IClientJobPostingsResponse,MarkJobReadyRequest } from '../../Types/Job'
import { Talent } from '../../Types/Talent';
import { buildUrl } from '../../Utils/Api';
import { AppSettings } from '../../Utils/Settings';

const base = 'job';
export const jobApi = createApi({
  reducerPath: 'jobApi',
  baseQuery: fetchBaseQuery({
    baseUrl: AppSettings.ApiBaseUrl,
    prepareHeaders: async (headers, { getState }) => {
      headers.set(
        'Authorization',
        `Bearer ${localStorage.getItem('token') ?? ''}`,
      );
      return headers;
    },
  }),
  tagTypes: ['Job', 'ManyJobs', 'JobsByStatus', 'JobPostings'],
  endpoints: (builder) => ({
      getJobs: builder.query<Job[], string>({
          query: (params) => ({
              url: buildUrl(base, `all${params}`),
              method: "GET",
          })
      }),
      getMarketplaceJobs: builder.query<IMarketplaceJob[], void>({
          query: () => ({
              url: buildUrl(base, `marketplace`),
              method: "GET"
          })
      }),
      getManyJobs: builder.query<IMarketplaceJob[], string[]>({
          providesTags: ['ManyJobs'],
          query: (body) => ({
              url: buildUrl(base, `many`),
              method: "POST",
              body
          })
      }),
      getJobsByStatus: builder.query<IJobByStatus[], {locator: string, statuses: string}>({
          providesTags: ['JobsByStatus'],
          query: ({locator, statuses}) => ({
              url: buildUrl(base, `${locator}/${statuses}`),
              method: "GET"
          })
      }),
      getJob: builder.query<Job, string>({
          providesTags: ['Job'],
          query: (locator) => ({
              url: buildUrl(base, `${locator}`),
              method: "GET"
          })
      }),
      getJobPostings: builder.query<IClientJobPostingsResponse , void>({
          providesTags: ['JobPostings'],
          query: () => ({
              url: buildUrl(base, `postings`),
              method: "GET"
          })
      }),
      addJob: builder.mutation<any, AddJobRequest>({
          invalidatesTags: ['Job', 'ManyJobs', 'JobsByStatus', 'JobPostings'],
          query: (body) => ({
              url: `/${base}`,
              method: "POST",
              body
          })
      }),
      updateJob: builder.mutation<any, any>({
          invalidatesTags: ['Job', 'JobsByStatus', 'ManyJobs', 'JobPostings'],
          query: (body) => ({
              url: `/${base}`,
              method: "PUT",
              body
          })
      }),
      inviteTalentToJob: builder.mutation<any, {job_locator: string, talent_locator: string, client_locator: string}>({
          query: (body) => ({
              url: buildUrl(base, `invite`),
              method: "POST",
              body
          })
      }),
      deleteJob: builder.mutation<any, string>({
          invalidatesTags: ['JobPostings'],
          query: (id) => ({
              url: buildUrl(base, `${id}`),
              method: "DELETE"
          })
      }),
      acceptJob: builder.mutation<void, AcceptJobRequest>({
          invalidatesTags: ['JobPostings'],
          query: ({locator, body}) => ({
              url: buildUrl(base, `accept/${locator}`),
              method: "POST",
              body
          })
      }),
      markJobReady: builder.mutation<void, MarkJobReadyRequest>({
          query: ({locator, body}) => ({
              url: buildUrl(base, `ready/${locator}`),
              method: "PUT",
              body
          })
      }),
      completeJob: builder.mutation<void, CompleteJobRequest>({
          query: ({locator, body}) => ({
              url: buildUrl(base, `accept/${locator}`),
              method: "PUT",
              body
          })
      }),
    })
});

export const {
    useGetJobQuery,
    useGetJobsQuery,
    useGetManyJobsQuery,
    useLazyGetJobsQuery,
    useAddJobMutation,
    useUpdateJobMutation,
    useDeleteJobMutation,
    useInviteTalentToJobMutation,
    useGetJobsByStatusQuery,
    useGetMarketplaceJobsQuery,
    useAcceptJobMutation,
    useMarkJobReadyMutation,
    useCompleteJobMutation,
    useGetJobPostingsQuery
} = jobApi
